import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
} from "@mui/material";
import { formatSummaryCurrency } from "../logic/currency-logic";
import { isZero } from "../logic/money";
import type { MonthlySummaryDto, SummaryItemDto } from "../api/types";
import { CenteredSpinner } from "./CenteredSpinner";

interface SummaryDisplayProps {
  summary: MonthlySummaryDto | undefined;
  isLoading: boolean;
  error: Error | null;
  viewMode: "groups" | "accounts" | "categories";
}

export const SummaryDisplay = ({
  summary,
  isLoading,
  error,
  viewMode,
}: SummaryDisplayProps) => {
  const getRows = (): SummaryItemDto[] => {
    if (!summary) return [];
    switch (viewMode) {
      case "groups":
        return summary.groups;
      case "accounts":
        return summary.accounts;
      case "categories":
        return summary.categories;
      default:
        return [];
    }
  };

  const rows = getRows();

  return (
    <>
      {isLoading && <CenteredSpinner />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      {summary && (
        <Stack spacing={4}>
          <Paper sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={4}
              sx={{
                justifyContent: "space-around",
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Income
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "success.main",
                  }}
                >
                  {formatSummaryCurrency(summary.totalIncome)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Expense
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "error.main",
                  }}
                >
                  {formatSummaryCurrency(summary.totalExpense)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Net
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {formatSummaryCurrency(summary.totalNet)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Income</TableCell>
                    <TableCell align="right">Expense</TableCell>
                    <TableCell align="right">Sum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell component="th" scope="row">
                        {row.id}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "success.main" }}>
                        {!isZero(row.income)
                          ? formatSummaryCurrency(row.income)
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {!isZero(row.expense)
                          ? formatSummaryCurrency(row.expense)
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatSummaryCurrency(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </>
  );
};
